import React, { useCallback, useMemo } from "react";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";
import {
    GRID_TREE_DATA_GROUPING_FIELD,
    GridColDef,
    GridComparatorFn,
    GridValueFormatterParams,
} from "@mui/x-data-grid-premium";

import { VehicleListItemDto } from "../../../models/transport/vehicle";
import { useTranslation } from "react-i18next";
import { VehicleEventInfo, VehicleEventType } from "../../../models/vehicle/vehicleEventInfo";
import { VehicleEventData } from "../../../hooks/reporting/useVehicleReportData";
import {
    FuelEmission,
    GroupingValue,
    setVehicleReportGrouping,
} from "../../../store/reportOptionsSlice";
import { useAppSelector } from "../../../framework/customStore";
import { ICustomerItem } from "../../../models/customer/customerItem";
import Grid2 from "@mui/material/Unstable_Grid2";
import { GroupedTreeDataGrid, GroupedDataGridRow } from "../../framework/groupedDataGrid/groupedTreeDataGrid";
import { ICostCenterItem } from "../../../models/costCenter/costCenterItem";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { UniqueIdentifier } from "@dnd-kit/core";
import { calculateAvgConsumption, calculateEmissionsFromDistanceToCo2Kg } from "../helpers/vehicleReportCalculations";
import { ReportGroupingPicker } from "../components/reportGroupingPicker";

export interface GridRow extends GroupedDataGridRow {
    distance: number;
    fuel: number;
    consumption: number;
    emissions: number;
}

interface GroupableVehicleReportGridProps {
    vehicles: VehicleListItemDto[];
    eventData: VehicleEventData;
    fuelEmissions: FuelEmission[];
    customers: ICustomerItem[];
    costCenters: ICostCenterItem[];
    employees: EmployeeListItemDto[];
    filterByCustomers: (val: string) => boolean;
    filterByCostCenters: (val: string) => boolean;
    filterByEmployees: (val: string) => boolean;
}

interface CompinedVehicleEvents {
    distances: VehicleEventInfo[];
    totalFuel: VehicleEventInfo[];
    vehicleId: string;
}

interface JoinedVehicleEventsDictionary {
    [Key: string]: CompinedVehicleEvents;
};

const groupedIdSplitChar = "_";

export const GroupableVehicleReportGrid = ({
    vehicles,
    eventData,
    fuelEmissions,
    customers,
    costCenters,
    employees,
    filterByCostCenters,
    filterByCustomers,
    filterByEmployees,
}: GroupableVehicleReportGridProps) => {
    const { t } = useTranslation();
    const selectedGrouping = useAppSelector((state) => state.reportOptions.vehicleGrouping);

    /// Check if event data is loaded, can be used to count on that some eventdata exists before using them
    const eventDataLoaded = useCallback(
        () => {
            return eventData[VehicleEventType.Distance] !== null && eventData[VehicleEventType.TotalFuel] !== null;
        }, [eventData]
    );

    // Resolve grouped column title from entities (eg. Vehicle register number and brand)
    // from each grouping depth and related type by given entity id
    // For customer and cost centers it is possible that all items are not available due to paged query used which might limit results
    // customer and cost center can then return translated "not found + id" message.
    const getGroupingColLabel = useCallback(
        (id: string, depth: number) => {

            if (selectedGrouping[depth] === GroupingValue.Vehicle as string) {
                if (!id) {
                    return t("reporting.noFleet");
                }

                const vehicle = vehicles?.find((v) => v.id === id);
                return `${vehicle?.registerNumber} - ${vehicle?.brand}`;
            }

            if (selectedGrouping[depth] === GroupingValue.Customer as string) {

                if (!id || !id.isGuid()) {
                    return t("reporting.noCustomer");
                }

                return (
                    customers?.find((c) => c.id === id)?.name ??
                    t("reporting.customerNotFound", { id: id })
                );
            }

            if (selectedGrouping[depth] === GroupingValue.Employee as string) {
                return employees?.find((e) => e.id === id)?.fullName ??
                    t("reporting.noEmployee");
            }

            if (selectedGrouping[depth] === GroupingValue.CostCenter as string) {
                if (!id || !id.isGuid()) {
                    return t("reporting.noCostCenter");
                }
                return (
                    costCenters?.find((v) => v.id === id)?.name ??
                    t("reporting.costCenterNotFound", { id: id })
                );
            }
        },
        [vehicles, customers, selectedGrouping]
    );

    // Build grid column definitions
    const gridDef: GridColDef<GridRow>[] = useMemo(
        () => [

            {
                field: "distance",
                headerName: t("reporting.distance"),
                description: t("reporting.distance"),
                groupable: false,
                type: "number",
                availableAggregationFunctions: ["sum"],
                minWidth: 100,
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams<number>) => {
                    const value = params.value;
                    if (!value || value < 0) return "-";
                    return value.toLocaleFixed(1, "fi-Fi");
                },
            },
            {
                field: "fuel",
                headerName: t("reporting.consumption"),
                description: t("reporting.consumption"),
                groupable: false,
                type: "number",
                availableAggregationFunctions: ["sum"],
                minWidth: 100,
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams<number>) => {
                    const value = params.value;
                    if (!value || value < 0) return "-";
                    return value.toLocaleFixed(1, "fi-Fi");
                },
            },
            {
                field: "consumption",
                headerName: t("reporting.avgConsumption"),
                description: t("reporting.avgConsumption"),
                groupable: false,
                type: "number",
                availableAggregationFunctions: ["sum"],
                minWidth: 100,
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams<number>) => {
                    const value = params.value;
                    if (!value || value < 0) return "-";
                    return value.toLocaleFixed(1, "fi-Fi");
                },
            },
            {
                field: "emissions",
                headerName: t("reporting.emissions"),
                description: t("reporting.emissions"),
                groupable: false,
                type: "number",
                availableAggregationFunctions: ["sum"],
                minWidth: 100,
                flex: 1,
                valueFormatter: (params: GridValueFormatterParams<number>) => {
                    const value = params.value;
                    if (!value || value < 0) return "-";
                    return value.toLocaleFixed(2, "fi-Fi");
                },
            },
        ],
        []
    );

    // Build grid rows.
    const gridRows: GridRow[] = useMemo(() => {
        if (selectedGrouping.length === 0 || !eventDataLoaded()) {
            return [];
        }

        // Apply filtering
        const filteredDistanceEvents = eventData[VehicleEventType.Distance].filter(
            (w) =>
                filterByCustomers(w?.customerId) &&
                filterByCostCenters(w?.costCenterId) &&
                filterByEmployees(w?.employeeId)
        );
        const filteredFuelEvents = eventData[VehicleEventType.TotalFuel].filter(
            (w) =>
                filterByCustomers(w?.customerId) &&
                filterByCostCenters(w?.costCenterId) &&
                filterByEmployees(w?.employeeId)
        );

        const filteredEventData : VehicleEventData = [filteredDistanceEvents, filteredFuelEvents];

        // Group data by selected groupings
        const joinedEventData: JoinedVehicleEventsDictionary =
            groupEventDataBySelectedGroupings(filteredEventData, selectedGrouping);

        // Create grid rows with grouping hierachy
        return _map(joinedEventData, (events, key) => {
            const distance = events.distances.reduce(
                (sum, e) => sum + (e.value || 0),
                0
            );
            const fuel = events.totalFuel.reduce(
                (sum, e) => sum + (e.value || 0),
                0
            );
            const consumption = calculateAvgConsumption(distance, fuel);
            const representativeVehicle = vehicles.find(
                (v) => v.id === events.vehicleId
            );
            const emissions = calculateEmissionsFromDistanceToCo2Kg(
                distance,
                representativeVehicle?.fuelType,
                fuelEmissions
            );

            const hiearchy = [...key.split(groupedIdSplitChar)];

            return {
                id: key,
                hierarchy: hiearchy,
                distance,
                fuel,
                consumption,
                emissions,
                totals: {
                    distance,
                    fuel,
                    consumption,
                    emissions,
                },
            };
        });

    }, [eventData,
        selectedGrouping,
        vehicles,
        fuelEmissions,
        eventDataLoaded,
        filterByCostCenters,
        filterByCustomers,
        filterByEmployees]);

    // This comparator sorts items by ID so that if ID is not present (Not available/generic group)
    // it will be last item within the group even asc/desc sorting
    const gridGroupColumnSortComparator: GridComparatorFn<any> = (v1, v2, cellParams1, cellParams2) => {
        // 1. Get the current sort direction from the DataGrid
        const sortModel = cellParams1.api.state.sorting.sortModel as {field; sort}[];
        const sortDirection = sortModel.find(m => m.field === cellParams1.field)?.sort || "asc";

        // 2. Identify rows that should always be last (e.g., non-GUIDs)
        const id1Parts = cellParams1.id.toString().split(/[_\/]/);
        const id2Parts = cellParams2.id.toString().split(/[_\/]/);

        // Take last id from id chain
        const id1 = id1Parts[id1Parts.length-1];
        const id2 = id2Parts[id2Parts.length-1];

        const id1IsGuid = id1.isGuid(); // Example: Non-GUIDs go last
        const Id2IsGuid = id2.isGuid();

        // 3. Force "last rows" to the end (regardless of sort direction)
        if (id1IsGuid && !Id2IsGuid) return sortDirection === "desc" ? 1 : -1;
        if (!id1IsGuid && Id2IsGuid) return sortDirection === "desc" ? -1 : 1;

        // 4. Apply normal sorting for non-last rows (respecting sort direction)
        let comparisonResult : number;
        if (typeof v1 === "string" && typeof v2 === "string") {
            comparisonResult = v1.localeCompare(v2);
        } else {
            comparisonResult = (v1 < v2 ? -1 : 1);
        }

        // 5. Reverse result if sorting is descending
        return comparisonResult;
    };

    const groupingOptions = useMemo(
        () => [
            GroupingValue.Employee,
            GroupingValue.Vehicle,
            GroupingValue.CostCenter,
            GroupingValue.Customer,
        ],
        []
    );

    return (
        <Grid2 container rowSpacing={2} pt={1}>
            {/* Grouping selectors */}
            <ReportGroupingPicker
                selectedGrouping={selectedGrouping}
                options={groupingOptions}
                setGrouping={setVehicleReportGrouping}
            />

            {/* The grouped data grid */}
            <Grid2 xs={12}>
                <GroupedTreeDataGrid
                    noRowsText={t("reporting.noVehicleEvents")}
                    columns={gridDef}
                    rows={gridRows}
                    detailsComponent={null}
                    defaultSortModel= {[{ field: GRID_TREE_DATA_GROUPING_FIELD, sort: "asc" }]}
                    groupingColDef={{
                        flex: 1,
                        sortable: true,
                        sortingOrder: ["asc", "desc"],
                        headerName: t("reporting.grouping"),
                        sortComparator: gridGroupColumnSortComparator,
                        valueFormatter: (data) => {
                            // for exports
                            const value = (data?.value ?? t("reporting.total"));
                            if ((value as string).isGuid()) {
                                return getGroupingColLabel(
                                    value as string,
                                    (data?.id as string).split("/").length - 2
                                );
                            } else {
                                // value is something else than guid
                                return value as string;
                            }
                        },
                    }}
                    getGroupLabel={getGroupingColLabel}
                    persistStateKey="GroupableVehicleEventGridState"
                    enableColumnSelector
                    showLeafLabelAsDate={false}
                />
            </Grid2>
        </Grid2>
    );

    /*

    Groups event data by the selected grouping fields.
    Example 1:
    If only customer is selected events grouping will be made within customers and key will be customerId
    Example 2:
    If three fields selected in order (vehicle, costcenter, customer), grouped data key will be vehicledId|costCenterId|customerId
    */
    function groupEventDataBySelectedGroupings(eventData: VehicleEventData, selectedGrouping: string[]) {

        // Group distances by the selected  fields.
        const groupedDistances = _groupBy(eventData[VehicleEventType.Distance], item => selectedGrouping.map(g => item[g] as UniqueIdentifier ?? g + "notAvailable").join(groupedIdSplitChar)
        );

        // Group total fuel by the selected  fields.
        const groupedFuel = _groupBy(eventData[VehicleEventType.TotalFuel], item => selectedGrouping.map(g => item[g] as UniqueIdentifier ?? g + "notAvailable").join(groupedIdSplitChar)
        );

        // Create a new object to hold the joined event data
        const joinedEventData: JoinedVehicleEventsDictionary = {};

        // Get all unique keys from both event types. Note that fuel and distance events will not be available at same timestamps
        const allKeys = new Set([
            ...Object.keys(groupedDistances),
            ...Object.keys(groupedFuel)
        ]);

        // Iterate through all keys and combine the data
        for (const key of allKeys) {

            // Resolve vehicleId from distance or fuel data (one or both should have data at this point)
            const vehicleId = groupedDistances[key]?.[0]?.vehicleId ?? groupedFuel[key][0].vehicleId;

            const compined: CompinedVehicleEvents = {
                // Distance data (empty array if none exists)
                distances: groupedDistances[key] || [],
                // Fuel data (empty array if none exists)
                totalFuel: groupedFuel[key] || [],
                // Store related vehicle id to simplify report row generation by providing vehciledId
                vehicleId: vehicleId
            };
            joinedEventData[key] = compined;

        }
        return joinedEventData;
    }
};
